import StaffContactDetails from 'app/shared-components/StaffContactDetails';
import SimpleDialog from '@fuse/core/SimpleDialog';
import {
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAbsencesByTypeQuery } from 'src/app/services/staffApi';
import { getAbsoluteFileUrl, normalizeFIO } from 'src/app/helpers/utils';
import moment from 'moment';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';

const absentTypes = {
  0: {
    uz: 'Barchasi',
    ru: 'Все',
  },
  1: {
    uz: 'Tatil',
    ru: 'Отпуск',
  },
  2: {
    uz: `Vaqtincha mehnatga layoqatsizlik varaqasi ma'lumoti`,
    ru: 'Больничный',
  },
  3: {
    uz: 'Xizmat safari',
    ru: 'Командировка',
  },
};

const AbsentList = () => {
  const { t, i18n } = useTranslation('translations');
  const [isEmplDetailsOpen, setIsEmplDetailsOpen] = useState(false);
  const [activeEmplId, setActiveEmplId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [value, setValue] = useState('1');

  const { data: AbsentData = {}, isLoading } = useGetAbsencesByTypeQuery({
    page: currentPage,
    rows: 5,
    type: value === '0' ? null : parseInt(value),
  });

  useEffect(() => {
    const root = document.getElementById('root');

    if (isEmplDetailsOpen) {
      root?.setAttribute('inert', '');
      document.body.style.overflow = 'hidden';
    } else {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    }

    return () => {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    };
  }, [isEmplDetailsOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [value]);

  let totalCount = 0;
  if (value === '0') {
    totalCount = AbsentData?.totalCount || 0;
  } else if (value === '1') {
    totalCount = AbsentData?.vacationsCount || 0;
  } else if (value === '2') {
    totalCount = AbsentData?.sicksCount || 0;
  } else if (value === '3') {
    totalCount = AbsentData?.workTripsCount || 0;
  }

  const totalPages = Math.ceil(totalCount / 5);

  const handleEmplClick = (id) => {
    setActiveEmplId(id);
    setIsEmplDetailsOpen(true);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const getAbsenceTypeLabel = (type, lang = 'uz') => absentTypes[type]?.[lang] || '';

  return (
    <>
      <Card
        component={motion.div}
        variants={{
          hidden: { opacity: 0, y: 40 },
          show: { opacity: 1, y: 0 },
        }}
        className="w-full mb-16 rounded-16 p-24 shadow-2"
      >
        <div className="flex items-start justify-between">
          <Typography component="p" className="flex flex-1 text-2xl leading-tight">
            {t('Vacations')}, {t('SickLeaves').toLowerCase()}, {t('BusinessTrips').toLowerCase()}{' '} ({AbsentData?.totalCount})
          </Typography>
          {/* <div>
            <FuseSvgIcon>react-icons:fa-regular-users</FuseSvgIcon>
            <p className="text-2xl">{AbsentData?.totalCount}</p>
          </div> */}
        </div>

        <div className="flex flex-wrap mt-20 border-[1px] p-4 rounded-md">
          <Button
            onClick={() => setValue('1')}
            variant="outlined"
            sx={{ border: 0 }}
            className={`flex-1 min-w-[60px] rounded-md text-base ${
              value === '1'
                ? 'text-white bg-blue hover:bg-blue'
                : 'text-darkgrey bg-white dark:bg-dark hover:bg-white'
            }`}
          >
            <FuseSvgIcon size={20}  className='mr-5'>react-outline:tropic</FuseSvgIcon> ({AbsentData?.vacationsCount})
          </Button>
          <Button
            onClick={() => setValue('2')}
            variant="outlined"
            sx={{ border: 0 }}
            className={`flex-1 min-w-[60px] rounded-md text-base ${
              value === '2'
                ? 'text-white bg-blue hover:bg-blue'
                : 'text-darkgrey bg-white dark:bg-dark hover:bg-white'
            }`}
          >
           <FuseSvgIcon size={20} className='mr-5'>react-outline:medicine</FuseSvgIcon> ({AbsentData?.sicksCount})
          </Button>
          <Button
            onClick={() => setValue('3')}
            variant="outlined"
            sx={{ border: 0 }}
            className={`flex-1 min-w-[60px] rounded-md text-base ${
              value === '3'
                ? 'text-white bg-blue hover:bg-blue'
                : 'text-darkgrey bg-white dark:bg-dark hover:bg-white'
            }`}
          >
           <FuseSvgIcon size={20} className='mr-5'>react-outline:work-trip</FuseSvgIcon> ({AbsentData?.workTripsCount})
          </Button>
        </div>

        <CardContent className="p-0 pt-12">
          {!isLoading && AbsentData?.data?.length === 0 && (
            <div className="text-center">{t('No')}</div>
          )}

          <List className="p-0">
            {AbsentData?.data?.map((absence, index) => (
              <ListItem
                key={`${absence.staffId}-${absence.start}-${absence.end}-${index}`}
                className="px-0 space-x-8 cursor-pointer hover:bg-grey-50 dark:hover:bg-lightblack border-b-1"
                onClick={() => handleEmplClick(absence.staffId)}
              >
                <Avatar src={getAbsoluteFileUrl(absence.path)} alt="employee photo">
                  {absence?.name.slice(0, 1)}
                </Avatar>
                <ListItemText>
                  <div className="flex flex-col">
                    <Typography
                      className="font-bold text-base font-lexend dark:text-white"
                      color="primary.main"
                      paragraph={false}
                    >
                      {normalizeFIO(absence.name)}
                    </Typography>
                    <Typography
                      className="text-sm font-normal dark:text-darkgrey"
                      paragraph={false}
                      color="primary.light"
                    >
                      {getAbsenceTypeLabel(absence.type, i18n.language)}
                    </Typography>
                    <Typography
                      className="text-sm font-normal dark:text-darkgrey"
                      paragraph={false}
                      color="primary.light"
                    >
                      {t('fromNumber', { val: moment(absence.start).format('DD.MM.YYYY') })}{' '}
                      {t('untilNumber', { val: moment(absence.end).format('DD.MM.YYYY') })}
                    </Typography>
                  </div>
                </ListItemText>
              </ListItem>
            ))}
          </List>

          {totalPages > 1 && (
            <div className="h-10 mt-20 flex justify-center items-center gap-4">
              <Button
                type="text"
                onClick={handlePrevPage}
                disabled={currentPage < 2}
                variant="text"
              >
                <ChevronLeft className="dark:text-white" />
              </Button>
              <span className="text-sm">
                {currentPage} / {totalPages}
              </span>
              <Button
                type="text"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                variant="text"
              >
                <ChevronRight className="dark:text-white" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <SimpleDialog open={isEmplDetailsOpen} onClose={() => setIsEmplDetailsOpen(false)} noPadding>
        <StaffContactDetails id={activeEmplId} />
      </SimpleDialog>
    </>
  );
};

export default AbsentList;
